/**
 * feat-362 — New-hire tracker (admin only).
 *
 * Lists Ladle & Leaf staff who clock in on Square but have never recorded in
 * Bespoke, with Mark followed up / Dismiss / Undo dismiss. Route
 * `/restaurant/administration/new-hires`, wrapped in `<AdminRoute>` in App.tsx;
 * this component does not re-gate. Modeled on CrewCalloutReviewQueue.
 *
 * Contract: CONTRACT.md r3 (feature root); r3 adds the server's store and the
 * "Bespoke activity" column (spec addendum r2). Design: design/README.md + _shared.js;
 * every class string in `C` / `IND` below is copied from `_shared.js`.
 *
 * Rules that matter:
 * - `indicator` comes from the API. Never recompute it.
 * - Rows arrive pre-sorted. Never re-sort; an action replaces its row in place.
 * - Every timestamp is formatted in `row.location.timezone`, never the browser tz.
 *   "N days ago" counts store-local calendar days against the response's `as_of`.
 * - Square names and admin notes are plain text only.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  Ban,
  Check,
  Clock,
  GraduationCap,
  MapPin,
  Mic,
  MicOff,
  PowerOff,
  RefreshCw,
  Undo2,
  UserCheck,
  UserPlus,
  UserX,
  Users,
} from 'lucide-react';
import ScorecardNavigation from '../ScorecardNavigation';
import {
  dismissNewHire,
  followUpNewHire,
  listNewHires,
  NewHireIndicator,
  NewHireList,
  NewHireRow,
  undismissNewHire,
} from '../../../services/newHiresAdminService';
import { ApiError } from '../../../utils/api';

// ============================================================================
// Copy — design/README.md §6
// ============================================================================

export const NEW_HIRES_COPY = {
  pageTitle: 'New hires',
  adminBadge: '🔒 Bespoke internal · admin only',
  pageSubtitle:
    "Ladle & Leaf staff who clock in on Square but have never recorded in Bespoke. Checked every day at 3 pm. Dates are in each store's local time.",
  breadcrumbParent: 'Bespoke Admin',
  breadcrumbSelf: 'New hires',
  hideDismissed: 'Hide dismissed',
  hiddenCount: (n: number) => `(${n} hidden)`,

  colName: 'New hire',
  colStatus: 'Status',
  colSquare: 'Square clock-ins',
  colServer: 'Bespoke server',
  colShifts: 'Bespoke activity',
  colFollowUp: 'Follow-up',
  colActions: 'Actions',
  first: 'First',
  last: 'Last',

  status: {
    red: 'Needs follow-up',
    amber: 'Followed up · not recording',
    neutral: 'Followed up',
    green: 'Recording',
    grey: 'Dismissed',
  } as Record<NewHireIndicator, string>,
  capRed: (ago: string) => `Detected ${ago}`,
  capAmber: (ago: string) => `Followed up ${ago}, no shift yet`,
  capNeutral: (ago: string) => `Followed up ${ago}`,
  capGreen: (date: string) => `Since ${date}`,
  capGrey: (date: string, email: string) => `${date} by ${email}`,
  today: 'today',
  oneDayAgo: '1 day ago',
  daysAgo: (n: number) => `${n} days ago`,

  noName: 'No Square name',
  autoCreated: 'Auto-created',
  wouldCreate: 'Would create (auto-create off)',
  createFailed: (err: string) => `Auto-create failed (${err})`,
  needsReview: 'Needs review — matches',
  reviewNameTaken: 'Needs review — a server with this name already exists at this store',
  reviewNoName: 'Needs review — no usable Square name',
  reviewInactiveMapping: 'Needs review — inactive POS mapping for',
  reviewInactiveMappingBare: 'Needs review — inactive POS mapping',
  noServer: 'No server',
  reasonUnmapped: 'When detected: not in Bespoke',
  reasonMapped: 'When detected: in Bespoke, never recorded',
  neverRecorded: 'Never recorded',
  differentStore: '· different store',
  differentStoreTitle: "This server belongs to a different store than the new hire's Square store",
  activityRecording: 'Recording',
  activityTraining: 'Training',
  activityOnboarding: 'Onboarding',
  lessonsDone: (n: number) => (n === 1 ? '1 lesson done' : `${n} lessons done`),
  trainingStarted: 'Started, none finished',
  trainingLast: (date: string) => `· last ${date}`,
  noTraining: 'No training yet',
  onboarded: 'Onboarded',
  onboardedNoConsent: 'Onboarded, no recording consent',
  notOnboarded: 'Not onboarded',
  activityNone: '—',
  followUpNone: 'Not yet',
  dismissedLabel: 'Dismissed',
  followedUpLine: (date: string, email: string) => `Followed up ${date} by ${email}`,
  nothingToDo: 'Nothing to do',

  markFollowedUp: 'Mark followed up',
  dismiss: 'Dismiss',
  undoDismiss: 'Undo dismiss',
  working: 'Working…',
  actionFailed: 'That did not save. Try again.',

  fuTitle: 'Mark followed up',
  fuTitleUpdate: 'Update follow-up',
  fuLastLine: (date: string, email: string) =>
    `Last follow-up ${date} by ${email}. Saving replaces the note and sets the date to today.`,
  fuNoteLabel: 'Note (required)',
  fuPlaceholder: 'Who you talked to and what happens next',
  fuNoteRequired: 'Add a note before saving.',
  fuSave: 'Save',
  fuSaving: 'Saving…',
  cancel: 'Cancel',

  dmTitle: (name: string) => `Dismiss ${name}?`,
  dmTitleNoName: 'Dismiss this new hire?',
  dmBody:
    'They stop showing as needing follow-up and are hidden while Hide dismissed is on. Their Bespoke server is not changed. You can undo this.',
  dmReasonLabel: 'Reason',
  dmDefaultReason: "Doesn't need a mic",
  dmReasonRequired: 'Add a reason before dismissing.',
  dmConfirm: 'Dismiss',
  dmBusy: 'Dismissing…',

  loading: 'Loading new hires…',
  emptyTitle: 'No new hires tracked yet.',
  emptyBody:
    'The daily check has not found anyone who clocks in on Square without recording in Bespoke. New people appear here the day after their first clock-in.',
  filteredTitle: 'Every tracked hire is dismissed.',
  filteredBody: (n: number) => `${n} dismissed hires are hidden.`,
  filteredAction: 'Show dismissed',
  errorTitle: 'Could not load new hires.',
  errorBody:
    "This is not the same as an empty list. We don't know who needs follow-up until it loads.",
  errorRetry: 'Try again',
  errorRequest: 'GET /admin/new-hires',
};

const COPY = NEW_HIRES_COPY;

// ============================================================================
// Class maps — design/_shared.js (static literals so Tailwind sees them)
// ============================================================================

const ICON = 'w-3.5 h-3.5 shrink-0';

const IND: Record<
  NewHireIndicator,
  { chip: string; stripe: string; Icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }> }
> = {
  red: {
    chip: 'bg-danger-surface text-danger-text',
    stripe: 'shadow-[inset_4px_0_0_0_#DC2626,1px_0_0_0_rgba(0,0,0,0.08)]',
    Icon: AlertCircle,
  },
  amber: {
    chip: 'bg-warning-surface text-ink',
    stripe: 'shadow-[inset_4px_0_0_0_#F59E0B,1px_0_0_0_rgba(0,0,0,0.08)]',
    Icon: Clock,
  },
  neutral: {
    chip: 'bg-cream-dark text-inkMuted ring-1 ring-inkMuted/20',
    stripe: 'shadow-[1px_0_0_0_rgba(0,0,0,0.08)]',
    Icon: Check,
  },
  green: {
    chip: 'bg-success-light text-success-on',
    stripe: 'shadow-[inset_4px_0_0_0_#5FA845,1px_0_0_0_rgba(0,0,0,0.08)]',
    Icon: Mic,
  },
  grey: {
    chip: 'bg-gray-100 text-inkMuted',
    stripe: 'shadow-[1px_0_0_0_rgba(0,0,0,0.08)]',
    Icon: Ban,
  },
};

const C = {
  page: 'max-w-[1600px] mx-auto p-4 md:p-8',
  h1: 'text-3xl font-display font-semibold text-ink',
  adminBadge:
    'inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-coral bg-coral-light/50 border border-coral/40 rounded-full px-2 py-0.5',
  sub: 'text-sm text-inkMuted',
  scroll: 'relative w-fit max-w-full overflow-x-auto rounded-2xl border border-softBorder bg-white',
  table: 'w-max border-separate border-spacing-0 text-left text-sm',
  th: 'bg-cream-dark px-3 py-2.5 text-[11px] font-bold uppercase tracking-wide text-inkMuted whitespace-nowrap border-l border-softBorder',
  thFrozen:
    'sticky left-0 z-30 bg-cream-dark px-3 py-2.5 text-[11px] font-bold uppercase tracking-wide text-inkMuted whitespace-nowrap shadow-[1px_0_0_0_rgba(0,0,0,0.08)]',
  td: 'px-3 py-3 border-t border-l border-softBorder align-top',
  tdFrozen: 'sticky left-0 z-20 px-3 py-3 pl-4 border-t border-softBorder align-top font-normal',
  chip: 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold whitespace-nowrap',
  factChip:
    'inline-flex items-center gap-1 rounded-full bg-white text-ink ring-1 ring-inkMuted/40 text-[11px] font-bold px-2 py-0.5 whitespace-nowrap',
  reviewChip:
    'inline-flex items-start gap-1 rounded-lg bg-danger-em text-danger-text text-[11px] font-bold px-2 py-1 leading-snug',
  caption: 'block text-[11px] text-inkMuted leading-snug',
  label: 'text-[11px] font-semibold uppercase tracking-wide text-inkMuted',
  mono: 'font-mono text-[11px] text-inkMuted break-all',
  link: 'text-ink font-semibold underline decoration-coral/70 decoration-2 underline-offset-2 hover:decoration-coral',
  reviewLink: 'underline decoration-danger-text/40 hover:decoration-danger-text',
  never: 'inline-flex items-center gap-1 text-inkMuted whitespace-nowrap',
  note: 'mt-1 text-sm text-ink/90 whitespace-pre-wrap break-words line-clamp-3',
  btnSecondary:
    'inline-flex items-center justify-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg border border-softBorder bg-white text-ink hover:bg-cream-dark focus:outline-none focus:ring-2 focus:ring-coral/40 whitespace-nowrap disabled:opacity-40',
  btnQuiet:
    'inline-flex items-center justify-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg text-inkMuted hover:bg-gray-100 hover:text-ink focus:outline-none focus:ring-2 focus:ring-coral/40 whitespace-nowrap',
  rowError: 'mt-2 text-xs text-danger-text',
  overlay: 'fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4',
  dialog: 'bg-white rounded-2xl border border-softBorder shadow-xl w-full max-w-md p-6',
  dlgTitle: 'font-display text-lg font-semibold text-ink break-words',
  dlgCancel: 'text-inkMuted text-sm font-semibold px-3 py-2 rounded-full hover:bg-gray-100',
  dlgConfirm:
    'bg-coral text-white font-display font-semibold rounded-full px-5 py-2 text-sm hover:brightness-95 shrink-0 disabled:opacity-40 disabled:cursor-not-allowed',
  textarea:
    'mt-1 w-full text-sm text-ink bg-white border border-gray-300 rounded-lg p-2 min-h-[6rem] focus:outline-none focus:ring-2 focus:ring-coral/40',
  input:
    'mt-1 w-full text-sm text-ink bg-white border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-coral/40',
  dlgAlert: 'mt-3 rounded-lg bg-danger-surface text-danger-text text-sm px-3 py-2',
};

const COLS = {
  name: 'w-[160px] min-w-[160px] md:w-[220px] md:min-w-[220px]',
  status: 'w-[200px] min-w-[200px]',
  square: 'w-[170px] min-w-[170px]',
  server: 'w-[220px] min-w-[220px]',
  shifts: 'w-[264px] min-w-[264px]',
  followup: 'w-[196px] min-w-[196px]',
  actions: 'w-[156px] min-w-[156px]',
};

// ============================================================================
// Formatting — every date in the row's store timezone (design/_shared.js)
// ============================================================================

function ymd(d: Date, tz: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

/** `Nov 3`, with the year only when it isn't `now`'s year (in the store tz). */
export function fmtDate(iso: string, tz: string, now: Date): string {
  const d = new Date(iso);
  const sameYear = ymd(d, tz).slice(0, 4) === ymd(now, tz).slice(0, 4);
  return d.toLocaleDateString('en-US', {
    timeZone: tz,
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

/** `Nov 3, 7:04 AM` in the store tz. */
export function fmtDateTime(iso: string, tz: string, now: Date): string {
  const time = new Date(iso).toLocaleTimeString('en-US', {
    timeZone: tz,
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${fmtDate(iso, tz, now)}, ${time}`;
}

/** Store-local calendar days between `iso` and `now` (yesterday 3 pm = 1). */
export function daysAgo(iso: string, tz: string, now: Date): number {
  return Math.round((Date.parse(ymd(now, tz)) - Date.parse(ymd(new Date(iso), tz))) / 86400000);
}

export function ago(iso: string, tz: string, now: Date): string {
  const n = daysAgo(iso, tz, now);
  if (n <= 0) return COPY.today;
  if (n === 1) return COPY.oneDayAgo;
  return COPY.daysAgo(n);
}

// ============================================================================
// Errors — 400/409 show the backend's `detail`; 422/500/other are generic
// ============================================================================

export function actionErrorMessage(e: unknown): string {
  if (e instanceof ApiError && (e.status === 400 || e.status === 409) && e.message) {
    return e.message;
  }
  return COPY.actionFailed;
}

// ============================================================================
// Links (CONTRACT.md r2)
// ============================================================================

const serverHref = (s: NonNullable<NewHireRow['server']>) =>
  `/tenant_management/${s.tenant_id}/locations/${s.location_id}`;
const candidateHref = (row: NewHireRow, c: NewHireRow['candidates'][number]) =>
  `/tenant_management/${row.tenant_id}/locations/${c.location_id}`;

const displayName = (row: NewHireRow) => row.square.display_name ?? COPY.noName;

// ============================================================================
// Cells
// ============================================================================

const TwoDates: React.FC<{ first: string | null; last: string | null; fmt: (s: string) => string; testId: string }> = ({
  first,
  last,
  fmt,
  testId,
}) => (
  <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 whitespace-nowrap tabular-nums" data-testid={testId}>
    <span className={`${C.label} leading-5`}>{COPY.first}</span>
    <span className="text-ink">{first ? fmt(first) : '—'}</span>
    <span className={`${C.label} leading-5`}>{COPY.last}</span>
    <span className="text-ink">{last ? fmt(last) : '—'}</span>
  </div>
);

function statusCaption(row: NewHireRow, now: Date): string {
  const tz = row.location.timezone;
  switch (row.indicator) {
    case 'amber':
      return row.followed_up ? COPY.capAmber(ago(row.followed_up.at, tz, now)) : '';
    case 'neutral':
      return row.followed_up ? COPY.capNeutral(ago(row.followed_up.at, tz, now)) : '';
    case 'green':
      // Q6: the first Bespoke shift, not `recorded_at`.
      return row.bespoke.first_shift_at ? COPY.capGreen(fmtDate(row.bespoke.first_shift_at, tz, now)) : '';
    case 'grey':
      return row.dismissed ? COPY.capGrey(fmtDate(row.dismissed.at, tz, now), row.dismissed.by) : '';
    case 'red':
    default:
      return COPY.capRed(ago(row.first_detected_at, tz, now));
  }
}

const StatusCell: React.FC<{ row: NewHireRow; now: Date }> = ({ row, now }) => {
  const ind = IND[row.indicator] ?? IND.red;
  const label = COPY.status[row.indicator] ?? row.status;
  const caption = statusCaption(row, now);
  return (
    <>
      <span className={`${C.chip} ${ind.chip}`} data-testid={`nh-status-${row.id}`} data-indicator={row.indicator}>
        <ind.Icon className={ICON} aria-hidden />
        {label}
      </span>
      {caption && <span className={`${C.caption} mt-1`}>{caption}</span>}
    </>
  );
};

const ServerCell: React.FC<{ row: NewHireRow }> = ({ row }) => {
  const reason = row.reason === 'unmapped' ? COPY.reasonUnmapped : COPY.reasonMapped;
  let top: React.ReactNode;
  if (row.server) {
    top = (
      <>
        <Link to={serverHref(row.server)} className={`${C.link} break-words`} data-testid={`nh-server-link-${row.id}`}>
          {row.server.name}
        </Link>
        <div className="text-xs text-inkMuted mt-0.5 break-words" data-testid={`nh-server-store-${row.id}`}>
          <MapPin className="inline-block w-3 h-3 mr-1 -mt-0.5 align-middle" aria-hidden />
          {row.server.location_name || '—'}
          {row.server.location_id !== row.location.id && (
            <>
              {' '}
              <span className="italic whitespace-nowrap" title={COPY.differentStoreTitle} data-testid={`nh-different-store-${row.id}`}>
                {COPY.differentStore}
              </span>
            </>
          )}
        </div>
        {row.auto_created && (
          <div className="mt-1">
            <span className={C.factChip} data-testid={`nh-auto-created-${row.id}`}>
              <UserPlus className="w-3 h-3 shrink-0" aria-hidden />
              {COPY.autoCreated}
            </span>
          </div>
        )}
      </>
    );
  } else {
    let sub: React.ReactNode = null;
    if (row.needs_review) {
      const links = row.candidates.map((c, i) => (
        <React.Fragment key={c.server_id}>
          {i > 0 && ', '}
          <Link to={candidateHref(row, c)} className={C.reviewLink} data-testid={`nh-candidate-link-${c.server_id}`}>
            {c.server_name}
          </Link>
        </React.Fragment>
      ));
      let text: React.ReactNode;
      switch (row.review_reason) {
        case 'name_taken':
          text = COPY.reviewNameTaken;
          break;
        case 'no_name':
          text = COPY.reviewNoName;
          break;
        case 'inactive_mapping':
          text = links.length ? (
            <>
              {COPY.reviewInactiveMapping} {links}
            </>
          ) : (
            COPY.reviewInactiveMappingBare
          );
          break;
        case 'name_match':
        default:
          text = links.length ? (
            <>
              {COPY.needsReview} {links}
            </>
          ) : (
            COPY.needsReview
          );
      }
      sub = (
        <span className={C.reviewChip} data-testid={`nh-review-${row.id}`} data-review-reason={row.review_reason ?? ''}>
          <Users className="w-3 h-3 mt-0.5 shrink-0" aria-hidden />
          <span>{text}</span>
        </span>
      );
    } else if (row.create_error !== null) {
      sub = (
        <span className={C.reviewChip} data-testid={`nh-create-error-${row.id}`}>
          <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" aria-hidden />
          <span>{COPY.createFailed(row.create_error)}</span>
        </span>
      );
    } else if (row.would_create) {
      sub = (
        <span className={C.factChip} data-testid={`nh-would-create-${row.id}`}>
          <PowerOff className="w-3 h-3 shrink-0" aria-hidden />
          {COPY.wouldCreate}
        </span>
      );
    }
    top = (
      <>
        <span className="text-inkMuted">{COPY.noServer}</span>
        {sub && <div className="mt-1">{sub}</div>}
      </>
    );
  }
  return (
    <>
      {top}
      <span className={`${C.caption} mt-1`}>{reason}</span>
    </>
  );
};

// "Bespoke activity" (r3): Recording / Training / Onboarding, one bullet each.
// Values come straight from the API; nothing is computed here but the copy.
const ACT_LI = 'flex items-start gap-1.5 text-inkMuted leading-5';
const ACT_ICON = `${ICON} mt-[3px]`;

function trainingText(row: NewHireRow, now: Date): React.ReactNode {
  const t = row.bespoke.training;
  if (!t) return COPY.activityNone;
  const tz = row.location.timezone;
  const last = (iso: string | null) =>
    iso ? <span className="whitespace-nowrap"> {COPY.trainingLast(fmtDate(iso, tz, now))}</span> : null;
  if (t.lessons_completed > 0) {
    return (
      <>
        {COPY.lessonsDone(t.lessons_completed)}
        {last(t.last_completed_at)}
      </>
    );
  }
  if (t.started) {
    return (
      <>
        {COPY.trainingStarted}
        {last(t.last_activity_at)}
      </>
    );
  }
  return COPY.noTraining;
}

function onboardingText(row: NewHireRow): string {
  const o = row.bespoke.onboarding;
  if (!o) return COPY.activityNone;
  if (o.onboarding_complete && o.consent_given) return COPY.onboarded;
  if (o.onboarding_complete) return COPY.onboardedNoConsent;
  return COPY.notOnboarded;
}

const ActivityCell: React.FC<{ row: NewHireRow; now: Date }> = ({ row, now }) => {
  const tz = row.location.timezone;
  const o = row.bespoke.onboarding;
  const OnbIcon = o?.onboarding_complete ? UserCheck : UserX;
  return (
    <ul className="space-y-1.5" data-testid={`nh-activity-${row.id}`}>
      <li className={ACT_LI}>
        <span className="sr-only">{COPY.activityRecording}: </span>
        {row.bespoke.first_shift_at ? (
          <>
            <Mic className={ACT_ICON} aria-hidden />
            <TwoDates
              first={row.bespoke.first_shift_at}
              last={row.bespoke.last_shift_at}
              fmt={(s) => fmtDate(s, tz, now)}
              testId={`nh-shifts-${row.id}`}
            />
          </>
        ) : (
          <span className={C.never} data-testid={`nh-never-${row.id}`}>
            <MicOff className={ICON} aria-hidden />
            {COPY.neverRecorded}
          </span>
        )}
      </li>
      <li className={ACT_LI}>
        <span className="sr-only">{COPY.activityTraining}: </span>
        <GraduationCap className={ACT_ICON} aria-hidden />
        <span data-testid={`nh-training-${row.id}`}>{trainingText(row, now)}</span>
      </li>
      <li className={ACT_LI}>
        <span className="sr-only">{COPY.activityOnboarding}: </span>
        <OnbIcon className={ACT_ICON} aria-hidden />
        <span data-testid={`nh-onboarding-${row.id}`}>{onboardingText(row)}</span>
      </li>
    </ul>
  );
};

const FollowUpCell: React.FC<{ row: NewHireRow; now: Date }> = ({ row, now }) => {
  const tz = row.location.timezone;
  const fu = row.followed_up;
  if (row.status === 'dismissed' && row.dismissed) {
    return (
      <>
        <div className={C.label}>{COPY.dismissedLabel}</div>
        <p className={C.note} title={row.dismissed.reason}>
          {row.dismissed.reason}
        </p>
        {fu && <span className={`${C.caption} mt-1`}>{COPY.followedUpLine(fmtDate(fu.at, tz, now), fu.by)}</span>}
      </>
    );
  }
  if (!fu) return <span className="text-inkMuted">{COPY.followUpNone}</span>;
  return (
    <>
      <div className="text-xs text-inkMuted break-words">
        {fmtDate(fu.at, tz, now)} · {fu.by}
      </div>
      <p className={C.note} title={fu.note}>
        {fu.note}
      </p>
    </>
  );
};

// ============================================================================
// Dialogs — PublishGuardDialog shell
// ============================================================================

interface DialogShellProps {
  titleId: string;
  busy: boolean;
  onClose: () => void;
  testId: string;
  children: React.ReactNode;
}

const DialogShell: React.FC<DialogShellProps> = ({ titleId, busy, onClose, testId, children }) => {
  // Esc and overlay click close, except while busy (feat-361 MoveDrillDialog rule).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);
  return (
    <div
      className={C.overlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
      data-testid={`${testId}-overlay`}
    >
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} className={C.dialog} data-testid={testId}>
        {children}
      </div>
    </div>
  );
};

const WhoLine: React.FC<{ row: NewHireRow }> = ({ row }) => (
  <p className="text-sm text-inkMuted mt-1">
    {displayName(row)} · {row.location.name}
  </p>
);

const FollowUpDialog: React.FC<{
  row: NewHireRow;
  now: Date;
  onClose: () => void;
  onSaved: (row: NewHireRow) => void;
}> = ({ row, now, onClose, onSaved }) => {
  const updating = row.followed_up !== null;
  const [note, setNote] = useState(updating ? row.followed_up!.note : '');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => ref.current?.focus(), []);
  const empty = note.trim() === '';

  const save = async () => {
    if (empty || busy) return;
    setBusy(true);
    setError(null);
    try {
      onSaved(await followUpNewHire(row.id, note));
    } catch (e) {
      setError(actionErrorMessage(e));
      setBusy(false);
    }
  };

  const tz = row.location.timezone;
  return (
    <DialogShell titleId="nh-fu-title" busy={busy} onClose={onClose} testId="nh-followup-dialog">
      <h2 id="nh-fu-title" className={C.dlgTitle}>
        {updating ? COPY.fuTitleUpdate : COPY.fuTitle}
      </h2>
      <WhoLine row={row} />
      {updating && (
        <p className="text-sm text-inkMuted mt-2" data-testid="nh-fu-last-line">
          {COPY.fuLastLine(fmtDate(row.followed_up!.at, tz, now), row.followed_up!.by)}
        </p>
      )}
      <label className="block mt-4 text-sm font-semibold text-ink" htmlFor="nh-fu-note">
        {COPY.fuNoteLabel}
      </label>
      <textarea
        id="nh-fu-note"
        ref={ref}
        className={C.textarea}
        placeholder={COPY.fuPlaceholder}
        value={note}
        maxLength={1000}
        disabled={busy}
        onChange={(e) => {
          setNote(e.target.value);
          setTouched(true);
        }}
        data-testid="nh-fu-note"
      />
      {touched && empty && <p className="mt-1 text-xs text-danger-text">{COPY.fuNoteRequired}</p>}
      {error && (
        <div role="alert" className={C.dlgAlert} data-testid="nh-fu-error">
          {error}
        </div>
      )}
      <div className="mt-5 flex justify-end gap-2">
        <button type="button" className={C.dlgCancel} onClick={onClose} disabled={busy}>
          {COPY.cancel}
        </button>
        <button type="button" className={C.dlgConfirm} onClick={save} disabled={empty || busy} data-testid="nh-fu-save">
          {busy ? COPY.fuSaving : COPY.fuSave}
        </button>
      </div>
    </DialogShell>
  );
};

const DismissDialog: React.FC<{
  row: NewHireRow;
  onClose: () => void;
  onSaved: (row: NewHireRow) => void;
}> = ({ row, onClose, onSaved }) => {
  const [reason, setReason] = useState(COPY.dmDefaultReason);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => ref.current?.focus(), []);
  const empty = reason.trim() === '';

  const confirm = async () => {
    if (empty || busy) return;
    setBusy(true);
    setError(null);
    try {
      onSaved(await dismissNewHire(row.id, reason));
    } catch (e) {
      setError(actionErrorMessage(e));
      setBusy(false);
    }
  };

  return (
    <DialogShell titleId="nh-dm-title" busy={busy} onClose={onClose} testId="nh-dismiss-dialog">
      <h2 id="nh-dm-title" className={C.dlgTitle}>
        {row.square.display_name ? COPY.dmTitle(row.square.display_name) : COPY.dmTitleNoName}
      </h2>
      <WhoLine row={row} />
      <p className="text-sm text-ink mt-3">{COPY.dmBody}</p>
      <label className="block mt-4 text-sm font-semibold text-ink" htmlFor="nh-dm-reason">
        {COPY.dmReasonLabel}
      </label>
      <input
        id="nh-dm-reason"
        ref={ref}
        className={C.input}
        value={reason}
        maxLength={255}
        disabled={busy}
        onChange={(e) => setReason(e.target.value)}
        data-testid="nh-dm-reason"
      />
      {empty && <p className="mt-1 text-xs text-danger-text">{COPY.dmReasonRequired}</p>}
      {error && (
        <div role="alert" className={C.dlgAlert} data-testid="nh-dm-error">
          {error}
        </div>
      )}
      <div className="mt-5 flex justify-end gap-2">
        <button type="button" className={C.dlgCancel} onClick={onClose} disabled={busy}>
          {COPY.cancel}
        </button>
        <button type="button" className={C.dlgConfirm} onClick={confirm} disabled={empty || busy} data-testid="nh-dm-confirm">
          {busy ? COPY.dmBusy : COPY.dmConfirm}
        </button>
      </div>
    </DialogShell>
  );
};

// ============================================================================
// Page
// ============================================================================

type DialogState = { kind: 'followup' | 'dismiss'; id: string } | null;

const NewHireTracker: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const hideDismissed = searchParams.get('dismissed') !== 'show';

  const [data, setData] = useState<NewHireList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; status: number | null } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [dialog, setDialog] = useState<DialogState>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await listNewHires());
    } catch (e) {
      setError({
        message: e instanceof Error ? e.message : String(e),
        status: e instanceof ApiError ? e.status : null,
      });
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setHideDismissed = (hide: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (hide) next.delete('dismissed');
    else next.set('dismissed', 'show');
    setSearchParams(next, { replace: true });
  };

  /** Replace one row in place with the server's version. No re-sort (brief §2.2). */
  const replaceRow = useCallback((updated: NewHireRow) => {
    setData((prev) =>
      prev ? { ...prev, rows: prev.rows.map((r) => (r.id === updated.id ? updated : r)) } : prev
    );
    setRowErrors((prev) => {
      if (!(updated.id in prev)) return prev;
      const next = { ...prev };
      delete next[updated.id];
      return next;
    });
  }, []);

  const undismiss = async (row: NewHireRow) => {
    setBusyId(row.id);
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[row.id];
      return next;
    });
    try {
      replaceRow(await undismissNewHire(row.id));
    } catch (e) {
      setRowErrors((prev) => ({ ...prev, [row.id]: actionErrorMessage(e) }));
    } finally {
      setBusyId(null);
    }
  };

  const now = useMemo(() => (data ? new Date(data.as_of) : new Date()), [data]);
  const allRows = data?.rows ?? [];
  const dismissedCount = allRows.filter((r) => r.status === 'dismissed').length;
  const visibleRows = hideDismissed ? allRows.filter((r) => r.status !== 'dismissed') : allRows;
  const hiddenCount = hideDismissed ? dismissedCount : 0;
  const dialogRow = dialog ? allRows.find((r) => r.id === dialog.id) ?? null : null;
  const closeDialog = useCallback(() => setDialog(null), []);
  const onDialogSaved = useCallback(
    (row: NewHireRow) => {
      replaceRow(row);
      setDialog(null);
    },
    [replaceRow]
  );

  const actions = (row: NewHireRow) => {
    const busy = busyId === row.id;
    const err = rowErrors[row.id];
    const errNode = err ? (
      <p className={C.rowError} data-testid={`nh-row-error-${row.id}`}>
        {err}
      </p>
    ) : null;
    if (row.status === 'dismissed') {
      return (
        <>
          <button
            type="button"
            className={C.btnSecondary}
            disabled={busy}
            onClick={() => undismiss(row)}
            data-testid={`nh-undismiss-${row.id}`}
          >
            <Undo2 className={ICON} aria-hidden />
            {busy ? COPY.working : COPY.undoDismiss}
          </button>
          {errNode}
        </>
      );
    }
    if (row.status === 'recording') {
      return <span className="text-inkMuted text-xs">{COPY.nothingToDo}</span>;
    }
    return (
      <>
        <div className="flex flex-col items-stretch gap-1.5">
          <button
            type="button"
            className={C.btnSecondary}
            onClick={() => setDialog({ kind: 'followup', id: row.id })}
            data-testid={`nh-followup-${row.id}`}
          >
            {COPY.markFollowedUp}
          </button>
          <button
            type="button"
            className={C.btnQuiet}
            onClick={() => setDialog({ kind: 'dismiss', id: row.id })}
            data-testid={`nh-dismiss-${row.id}`}
          >
            {COPY.dismiss}
          </button>
        </div>
        {errNode}
      </>
    );
  };

  let body: React.ReactNode;
  if (loading) {
    body = (
      <div data-testid="nh-loading">
        <p role="status" className="text-sm text-inkMuted mb-3">
          {COPY.loading}
        </p>
        <div className="space-y-2" aria-hidden>
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-12 rounded-lg bg-cream-dark" />
          ))}
        </div>
      </div>
    );
  } else if (error) {
    body = (
      <div className="rounded-2xl border border-danger-line bg-danger-surface p-5" role="alert" data-testid="nh-error">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-danger-text shrink-0 mt-0.5" aria-hidden />
          <div className="min-w-0">
            <p className="font-semibold text-danger-text">{COPY.errorTitle}</p>
            <p className="text-sm text-ink mt-1">{COPY.errorBody}</p>
            <p className="font-mono text-[11px] text-inkMuted mt-2 break-all">
              {COPY.errorRequest} → {error.status ?? '—'} {error.message}
            </p>
            <button type="button" className={`${C.btnSecondary} mt-3`} onClick={load} data-testid="nh-retry">
              <RefreshCw className={ICON} aria-hidden />
              {COPY.errorRetry}
            </button>
          </div>
        </div>
      </div>
    );
  } else if (allRows.length === 0) {
    body = (
      <div className="rounded-2xl border border-softBorder bg-white p-6 text-center" data-testid="nh-empty">
        <Users className="w-6 h-6 text-inkMuted mx-auto mb-2" aria-hidden />
        <p className="font-semibold text-ink">{COPY.emptyTitle}</p>
        <p className="text-sm text-inkMuted mt-1 max-w-xl mx-auto">{COPY.emptyBody}</p>
      </div>
    );
  } else if (visibleRows.length === 0) {
    body = (
      <div className="rounded-2xl border border-softBorder bg-cream-dark p-6 text-center" data-testid="nh-filtered-empty">
        <Ban className="w-6 h-6 text-inkMuted mx-auto mb-2" aria-hidden />
        <p className="font-semibold text-ink">{COPY.filteredTitle}</p>
        <p className="text-sm text-inkMuted mt-1">{COPY.filteredBody(dismissedCount)}</p>
        <button type="button" className={`${C.btnSecondary} mt-3`} onClick={() => setHideDismissed(false)}>
          {COPY.filteredAction}
        </button>
      </div>
    );
  } else {
    body = (
      <div className="min-w-0">
        <div className={C.scroll}>
          <table className={C.table} data-testid="nh-table">
            <thead>
              <tr>
                <th scope="col" className={`${C.thFrozen} ${COLS.name}`}>
                  {COPY.colName}
                </th>
                <th scope="col" className={`${C.th} ${COLS.status}`}>
                  {COPY.colStatus}
                </th>
                <th scope="col" className={`${C.th} ${COLS.square}`}>
                  {COPY.colSquare}
                </th>
                <th scope="col" className={`${C.th} ${COLS.server}`}>
                  {COPY.colServer}
                </th>
                <th scope="col" className={`${C.th} ${COLS.shifts}`}>
                  {COPY.colShifts}
                </th>
                <th scope="col" className={`${C.th} ${COLS.followup}`}>
                  {COPY.colFollowUp}
                </th>
                <th scope="col" className={`${C.th} ${COLS.actions}`}>
                  {COPY.colActions}
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => {
                const tz = row.location.timezone;
                const bg = row.indicator === 'grey' ? 'bg-gray-50' : 'bg-white';
                const stripe = (IND[row.indicator] ?? IND.red).stripe;
                return (
                  <tr key={row.id} data-testid={`nh-row-${row.id}`} data-indicator={row.indicator}>
                    <th scope="row" className={`${C.tdFrozen} ${bg} ${stripe}`}>
                      <div className={row.square.display_name ? 'font-semibold text-ink break-words' : 'italic text-inkMuted break-words'}>{displayName(row)}</div>
                      <div className="flex items-center gap-1 text-xs text-inkMuted mt-0.5">
                        <MapPin className="w-3 h-3 shrink-0" aria-hidden />
                        {row.location.name}
                      </div>
                      <div className={`${C.mono} mt-1`} title="Square team member id">
                        {row.square.team_member_id}
                      </div>
                    </th>
                    <td className={`${C.td} ${bg}`}>
                      <StatusCell row={row} now={now} />
                    </td>
                    <td className={`${C.td} ${bg}`}>
                      <TwoDates
                        first={row.square.first_clock_in_at}
                        last={row.square.last_clock_in_at}
                        fmt={(s) => fmtDateTime(s, tz, now)}
                        testId={`nh-clockins-${row.id}`}
                      />
                    </td>
                    <td className={`${C.td} ${bg}`}>
                      <ServerCell row={row} />
                    </td>
                    <td className={`${C.td} ${bg}`}>
                      <ActivityCell row={row} now={now} />
                    </td>
                    <td className={`${C.td} ${bg}`}>
                      <FollowUpCell row={row} now={now} />
                    </td>
                    <td className={`${C.td} ${bg}`}>{actions(row)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream" data-testid="new-hire-tracker">
      <ScorecardNavigation />
      <div className={C.page}>
        <header className="mb-5">
          <div className="text-xs text-inkMuted mb-1">
            <Link className="hover:underline" to="/restaurant/internal">
              {COPY.breadcrumbParent}
            </Link>{' '}
            / {COPY.breadcrumbSelf}
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h1 className={C.h1}>{COPY.pageTitle}</h1>
            <span className={C.adminBadge}>{COPY.adminBadge}</span>
          </div>
          <p className={`${C.sub} max-w-3xl`}>{COPY.pageSubtitle}</p>
        </header>
        <div className="mb-3 flex flex-wrap items-center gap-4">
          <label className="inline-flex items-center gap-2 text-sm text-ink cursor-pointer">
            <input
              type="checkbox"
              className="h-4 w-4 accent-coral"
              checked={hideDismissed}
              onChange={(e) => setHideDismissed(e.target.checked)}
              data-testid="nh-hide-dismissed"
            />
            {COPY.hideDismissed}
            {hiddenCount > 0 && <span className="text-inkMuted">{COPY.hiddenCount(hiddenCount)}</span>}
          </label>
        </div>
        {body}
      </div>
      {dialog && dialogRow && dialog.kind === 'followup' && (
        <FollowUpDialog row={dialogRow} now={now} onClose={closeDialog} onSaved={onDialogSaved} />
      )}
      {dialog && dialogRow && dialog.kind === 'dismiss' && (
        <DismissDialog row={dialogRow} onClose={closeDialog} onSaved={onDialogSaved} />
      )}
    </div>
  );
};

export default NewHireTracker;
